<script setup lang="ts">
import { CheckIcon, ClockIcon, TruckIcon } from 'lucide-vue-next'
import { useIncomingCollects } from '~/composables/collect/useIncomingCollects'
import { AscDescGraphql, CollectStatus } from '~/types/graphql-backend-types/gql-types'
import { formatDateWithI18n } from '~/utils/date'

const { t } = useI18n()
const router = useRouter()

const filters = computed(() => {
  const today = new Date()
  const dayOfWeek = today.getDay()

  // Get Monday (first day of week)
  // If Sunday (0), we need to go back 6 days
  // If Monday (1), we go back 0 days, etc.
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const monday = new Date(today)
  monday.setDate(today.getDate() + mondayOffset)
  monday.setHours(0, 0, 0, 0)

  // Get Sunday (last day of week)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  return {
    startsAtFrom: monday,
    startsAtTo: sunday,
    orderBy: { field: 'startsAt', order: AscDescGraphql.Desc },
  }
})
const { data: incomingCollects, isPending } = useIncomingCollects(filters, 1)

const remainingIncomingCollects = computed(() => {
  if (incomingCollects.value) {
    const doneCollects = incomingCollects.value.collection.filter(collect => collect?.status === CollectStatus.Finished)
    return incomingCollects.value.count - doneCollects.length
  }
  return 0
})

function redirectToCollectsWithCorrectFilters() {
  const dateQueryParamStartsAt = filters.value.startsAtFrom.toISOString().split('T')[0]
  const dateQueryParamEndsAt = filters.value.startsAtTo.toISOString().split('T')[0]
  router.push(`/collects?startsAt=${dateQueryParamStartsAt}&endsAt=${dateQueryParamEndsAt}`)
}
</script>

<template>
  <Card v-if="!isPending" class="flex grow">
    <template #title>
      <TruckIcon :size="24" />
      {{ t('global.incoming_collects', { count: remainingIncomingCollects }) }}

      <div class="ml-auto">
        <EButton
          v-if="incomingCollects?.collection.length && incomingCollects?.collection.length > 0"
          :label="t('global.see_all')"
          class="btn-primary btn-outline"
          size="sm"
          @click="redirectToCollectsWithCorrectFilters"
        />
      </div>
    </template>
    <template #content>
      <div class="bg-base-200 p-4 rounded-lg flex flex-col gap-2">
        <div v-if="incomingCollects?.collection.length === 0">
          {{ t('global.no_incoming_collects') }}
        </div>
        <div
          v-for="collect in incomingCollects?.collection.slice(0, 5)"
          :key="collect?.id"
        >
          <div class="flex justify-between gap-2">
            <div>
              {{ formatDateWithI18n(collect?.startsAt, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour12: false,
                timeZone: "UTC",
              }) }}
            </div>
            <div class="badge badge-sm gap-2" :class="collect?.status === CollectStatus.Finished ? 'badge-success' : 'badge-warning'">
              <CheckIcon v-if="collect?.status === CollectStatus.Finished" :size="12" />
              <ClockIcon v-else :size="12" />
              {{ collect?.status === CollectStatus.Finished ? t('collect.collected') : t('global.coming_soon') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>

  <div v-else class="skeleton h-full w-full" />
</template>
